import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, Loading, usePersistedState, BackButton, BadgeSignatureFormateur } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

function ModalNouveauFormateur({ onSave, onClose }) {
  const [form, setForm] = useState({
    nom_formateur: '', societe: '', email: '', tel: '',
    adresse1: '', adresse2: '', cp: '', ville: '',
    contrat_ref: '', actif: true, type_formateur: 'vacataire',
    organisme_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [organismes, setOrganismes] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    apiFetch('/organismes-formation').then(r => setOrganismes(r.data || [])).catch(() => {});
  }, []);

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' };
  const fieldWrap = { marginBottom: '0.7rem' };

  function field(label, key, type = 'text') {
    return (
      <div style={fieldWrap}>
        <label style={labelStyle}>{label}</label>
        <input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  async function handleSave() {
    if (!form.nom_formateur.trim()) return setError('Le nom est requis');
    setSaving(true); setError('');
    try {
      await apiFetch('/formateurs', { method: 'POST', body: JSON.stringify(form) });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '600px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>+ Nouveau formateur</h3>

        {/* Type interne / vacataire / of */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.2rem' }}>
          {[{ val: 'interne', label: '👤 Interne' }, { val: 'vacataire', label: '🏢 Vacataire' }, { val: 'of', label: '🏛 Organisme de Formation' }].map(t => (
            <button key={t.val} type="button" onClick={() => set('type_formateur', t.val)}
              style={{ flex: 1, padding: '0.55rem', borderRadius: '8px', border: '2px solid', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
                borderColor: form.type_formateur === t.val ? '#1a3668' : 'var(--border)',
                background: form.type_formateur === t.val ? 'rgba(26,54,104,0.08)' : 'var(--bg-elevated)',
                color: form.type_formateur === t.val ? '#1a3668' : 'var(--text-secondary)' }}>
              {t.label}
            </button>
          ))}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Identité</p>
        {field('Nom complet *', 'nom_formateur')}
        {form.type_formateur !== 'of' && field('Société / Organisme', 'societe')}
        {form.type_formateur === 'of' && (
          <div style={fieldWrap}>
            <label style={labelStyle}>Organisme de Formation *</label>
            <select
              value={form.organisme_id || ''}
              onChange={e => set('organisme_id', e.target.value ? Number(e.target.value) : null)}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">— Sélectionner un organisme —</option>
              {organismes.map(o => (
                <option key={o.id} value={o.id}>{o.nom}</option>
              ))}
            </select>
            {organismes.length === 0 && (
              <p style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '0.3rem' }}>
                Aucun OF enregistré — créez-en un dans Paramètres &gt; Organismes de Formation
              </p>
            )}
          </div>
        )}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contact</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
          {field('Email', 'email', 'email')}
          {field('Téléphone', 'tel', 'tel')}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Adresse</p>
        {field('Adresse ligne 1', 'adresse1')}
        {field('Adresse ligne 2', 'adresse2')}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 0.7rem' }}>
          {field('Code postal', 'cp')}
          {field('Ville', 'ville')}
        </div>

        {form.type_formateur !== 'interne' && (
          <>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contrat</p>
            <div style={fieldWrap}>
              <label style={labelStyle}>Référence contrat</label>
              <textarea value={form.contrat_ref} onChange={e => set('contrat_ref', e.target.value)} rows={2}
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '1rem' }}>
          <input type="checkbox" checked={form.actif} onChange={e => set('actif', e.target.checked)} />
          Formateur actif
        </label>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Créer le formateur'}
          </button>
        </div>
      </div>
    </div>
  );
}

const TYPE_BADGE = {
  interne: { label: 'Interne', bg: 'rgba(16,185,129,0.1)', color: '#059669' },
  vacataire: { label: 'Vacataire', bg: 'rgba(245,158,11,0.1)', color: '#d97706' },
  of: { label: 'OF', bg: 'rgba(139,92,246,0.1)', color: '#7c3aed' },
};

export default function Formateurs() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [archives, setArchives] = usePersistedState('formateurs_archives', false);
  const [formationFilter, setFormationFilter] = usePersistedState('formateurs_formation', '');
  const [typeFilter, setTypeFilter] = usePersistedState('formateurs_type', '');
  const [signatureFilter, setSignatureFilter] = usePersistedState('formateurs_signature', '');
  const [modal, setModal] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState(() => localStorage.getItem('formateurs_view') || 'carte');

  function toggleView(v) {
    setView(v);
    localStorage.setItem('formateurs_view', v);
  }
  const { data, loading } = useApi('/formateurs', { archives: archives ? 'true' : undefined }, [archives, refresh]);

  const allFormations = useMemo(() => {
    const map = new Map();
    (data?.data || []).forEach(f =>
      (f.formations || []).forEach(fo => fo?.formation_id && map.set(fo.formation_id, fo.intitule))
    );
    return [...map.entries()].sort((a, b) => (a[1] || '').localeCompare(b[1] || '', 'fr'));
  }, [data]);

  const formateurs = useMemo(() => {
    let list = [...(data?.data || [])];
    if (formationFilter) list = list.filter(f => (f.formations || []).some(fo => String(fo.formation_id) === formationFilter));
    if (typeFilter) list = list.filter(f => f.type_formateur === typeFilter);
    if (signatureFilter) list = list.filter(f => (f.signature?.etat || 'aucune') === signatureFilter);
    return list.sort((a, b) =>
      ((a.nom_formateur || '').split(' ').pop() || '').localeCompare(
        ((b.nom_formateur || '').split(' ').pop() || ''), 'fr'
      )
    );
  }, [data, formationFilter, typeFilter, signatureFilter]);

  if (loading) return <Loading />;

  return (
    <div className="page">
      <PageHeader
        title="Formateurs"
        subtitle={`${formateurs.length} formateur${formateurs.length !== 1 ? 's' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate('/organismes-formation')} style={{ fontSize: '0.85rem' }}>
              🏛 Organismes de Formation
            </button>
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={() => setModal(true)}>+ Nouveau formateur</button>
            )}
          </div>
        }
      />

      <div className="toolbar">
        <div style={{ flex: 1 }} />
        <div className="toolbar-filters">
          <select className="filter-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">Tous types</option>
            <option value="interne">Internes</option>
            <option value="vacataire">Vacataires</option>
            <option value="of">Organismes de Formation</option>
          </select>
          <select className="filter-select" value={signatureFilter} onChange={e => setSignatureFilter(e.target.value)} title="Signature numérisée pour les attestations">
            <option value="">Toutes signatures</option>
            <option value="valide">✍ Signature enregistrée</option>
            <option value="a_renouveler">⚠ À renouveler</option>
            <option value="en_attente">⏳ Demande en attente</option>
            <option value="aucune">— Sans signature</option>
          </select>
          <select
            className="filter-select"
            value={formationFilter}
            onChange={e => setFormationFilter(e.target.value)}
            style={{ minWidth: '200px' }}
          >
            <option value="">Toutes les formations</option>
            {allFormations.map(([id, intitule]) => (
              <option key={id} value={String(id)}>{intitule}</option>
            ))}
          </select>
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => setArchives(a => !a)}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivés
          </button>

          {/* Bascule vue carte / liste */}
          <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid var(--border)', overflow: 'hidden' }}>
            <button
              title="Vue cartes"
              onClick={() => toggleView('carte')}
              style={{
                padding: '0.35rem 0.65rem', border: 'none', cursor: 'pointer', fontSize: '0.95rem',
                background: view === 'carte' ? 'var(--accent)' : 'var(--bg-elevated)',
                color: view === 'carte' ? 'white' : 'var(--text-secondary)',
                transition: 'background 0.15s',
              }}>⊞</button>
            <button
              title="Vue liste"
              onClick={() => toggleView('liste')}
              style={{
                padding: '0.35rem 0.65rem', border: 'none', borderLeft: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.95rem',
                background: view === 'liste' ? 'var(--accent)' : 'var(--bg-elevated)',
                color: view === 'liste' ? 'white' : 'var(--text-secondary)',
                transition: 'background 0.15s',
              }}>☰</button>
          </div>
        </div>
      </div>

      {formateurs.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', borderRadius: '10px', fontSize: '0.9rem', marginTop: '1rem' }}>
          {formationFilter
            ? 'Aucun formateur ne dispense cette formation. Vérifiez les formations dispensées dans la fiche formateur.'
            : 'Aucun formateur trouvé.'}
        </div>
      )}

      {/* ── Vue cartes ── */}
      {view === 'carte' && (
        <div className="formateurs-grid">
          {formateurs.map(f => (
            <div key={f.formateur_id} className="card formateur-card"
              onClick={() => navigate(`/formateurs/${f.formateur_id}`)}
              style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>

              <div className="formateur-header">
                <div className="formateur-avatar">
                  {f.nom_formateur?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 className="formateur-name">{f.nom_formateur}</h3>
                  <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {f.societe && <span className="formateur-societe">{f.societe}</span>}
                    {f.type_formateur && TYPE_BADGE[f.type_formateur] && (
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', padding: '0.05rem 0.35rem', background: TYPE_BADGE[f.type_formateur].bg, color: TYPE_BADGE[f.type_formateur].color }}>
                        {TYPE_BADGE[f.type_formateur].label}
                      </span>
                    )}
                    {f.type_formateur === 'of' && f.organisme_nom && (
                      <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>{f.organisme_nom}</span>
                    )}
                  </div>
                </div>
                <BadgeSignatureFormateur signature={f.signature} />
                <Badge variant={f.actif ? 'badge-success' : 'badge-danger'}>
                  {f.actif ? 'Actif' : 'Inactif'}
                </Badge>
              </div>

              <div className="formateur-info">
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {f.email && <div className="formateur-detail">✉ {f.email}</div>}
                  {f.tel   && <div className="formateur-detail">☎ {f.tel}</div>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '1px solid var(--border)' }}>
                  {f.ville ? <div className="formateur-detail" style={{ margin: 0 }}>⌂ {f.cp} {f.ville}</div> : <span />}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Voir la fiche →</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Vue liste ── */}
      {view === 'liste' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Ville</th>
                <th style={{ textAlign: 'center' }}>Formations</th>
                <th style={{ textAlign: 'center' }} title="Signature numérisée pour les attestations">Signature</th>
                <th style={{ textAlign: 'center' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {formateurs.map(f => (
                <tr key={f.formateur_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/formateurs/${f.formateur_id}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: 'var(--accent)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        {f.nom_formateur?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{f.nom_formateur}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {f.type_formateur && TYPE_BADGE[f.type_formateur] && (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, borderRadius: '3px', padding: '0.1rem 0.35rem', background: TYPE_BADGE[f.type_formateur].bg, color: TYPE_BADGE[f.type_formateur].color }}>
                          {TYPE_BADGE[f.type_formateur].label}
                        </span>
                      )}
                      {f.type_formateur === 'of' && (f.organisme_nom || f.societe) && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{f.organisme_nom || f.societe}</span>
                      )}
                      {f.type_formateur !== 'of' && f.societe && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{f.societe}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{f.email || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{f.tel || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                    {f.ville ? `${f.cp || ''} ${f.ville}`.trim() : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {(f.formations || []).length > 0 ? (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        {(f.formations || []).length} formation{(f.formations || []).length > 1 ? 's' : ''}
                      </span>
                    ) : <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}><BadgeSignatureFormateur signature={f.signature} /></td>
                  <td style={{ textAlign: 'center' }}>
                    <Badge variant={f.actif ? 'badge-success' : 'badge-danger'}>
                      {f.actif ? 'Actif' : 'Inactif'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ModalNouveauFormateur
          onSave={() => { setModal(false); setRefresh(r => r + 1); }}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}
